import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { IntlShape } from '@atlassian/jira-intl/src/index.tsx';
import { dynamicColorToToken } from '../utils/colors/index.tsx';
import {
	LINE,
	HOUR_TIME_SCALES,
	WEEK_TIME_SCALES,
	MONTH_TIME_SCALES,
	TIME_SCALES,
	SEARCH_BY,
} from './constants';
import { getLabel, getMetaData, formatDateISO } from './date-formatter-utils';
import type { Query, ChartData, SummaryResponse, CustomSummaryResponse } from './types';
import { getYAxisMinMax, getValueFormatter, isPercentageChart, getTargetMarkLine } from './utils';

export const isCustomTimeScale = (timescaleId: number): boolean =>
	timescaleId === TIME_SCALES.customTimeScale;

const formatChartData = (
	source: SummaryResponse | CustomSummaryResponse,
	searchQueryString: string,
): ChartData => {
	const result: ChartData = {
		id: '',
		label: '',
		timezone: '',
		startDate: '',
		endDate: '',
		// @ts-expect-error - TS2322 - Type '""' is not assignable to type '"d" | "m" | "h" | "w"'.
		searchBy: '',
		axisLabels: [],
		chartSeries: [],
		isPercentage: false,
		yAxisMinMax: {},
	};

	result.id = source.reportId;
	result.label = source.name;
	result.timezone = source.timezone;
	try {
		result.startDate = formatDateISO(source.startTime, source.timezone);
		result.endDate = formatDateISO(source.endTime, source.timezone);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		fireErrorAnalytics({
			meta: {
				id: 'formatChartData',
				packageName: 'jiraServicedeskReportsCommon',
				teamName: 'itsm-saga',
			},
			sendToPrivacyUnsafeSplunk: true,
			error: err,
			attributes: {
				startDate: source.startTime,
				endDate: source.endTime,
				timezone: source.timezone,
			},
		});

		throw err;
	}

	// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"8" | "9m"'.
	if (MONTH_TIME_SCALES.includes(searchQueryString)) {
		result.searchBy = SEARCH_BY.monthly;
		// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"6" | "7" | "9w"'.
	} else if (WEEK_TIME_SCALES.includes(searchQueryString)) {
		result.searchBy = SEARCH_BY.weekly;
		// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"1" | "9h"'.
	} else if (HOUR_TIME_SCALES.includes(searchQueryString)) {
		result.searchBy = SEARCH_BY.hourly;
	} else {
		result.searchBy = SEARCH_BY.daily;
	}
	return result;
};

export const transformSummarySource = (
	source: SummaryResponse,
	searchParams: Query,
	intl: IntlShape,
): ChartData => {
	const searchQueryString = isCustomTimeScale(searchParams.timescaleId)
		? `${searchParams.timescaleId}${searchParams.timeBreakdown || ''}`
		: `${searchParams.timescaleId}`;

	const result = formatChartData(source, searchQueryString);
	result.yAxisMinMax = getYAxisMinMax(source);

	const formatter = getValueFormatter(source);

	// map source series to chart Item series
	const chartItems = source.series.map((s, seriesIndex) => ({
		type: LINE,
		seriesIndex,
		seriesId: s.seriesId,
		name: s.label,
		summaryValue: formatter(s.seriesSummaryValue),
		itemStyle: {
			color: dynamicColorToToken(s.color),
			borderWidth: 2,
			shadowColor: dynamicColorToToken(s.color),
		},
		showAllSymbol: true,
		emphasis: {
			itemStyle: {
				borderColor: dynamicColorToToken(s.color),
				shadowColor: dynamicColorToToken(s.color),
				borderWidth: 7,
				shadowBlur: 0,
			},
		},
		symbolSize: 9,
		data: s.data.map(({ x, y }, dataIndex) => ({
			id: x,
			seriesId: s.seriesId,
			seriesIndex,
			dataIndex,
			label: getLabel(x, source.timezone, searchQueryString, intl),
			...formatter(y),
			...getMetaData(x, source.timezone, searchQueryString, intl),
		})),
	}));
	// @ts-expect-error - TS2322 - Type '{ type: string; seriesIndex: number; seriesId: string; name: string; summaryValue: YAxisData; itemStyle: { color: string; borderWidth: number; shadowColor: string; }; showAllSymbol: boolean; emphasis: { ...; }; symbolSize: number; data: { ...; }[]; }[]' is not assignable to type 'Series[]'.
	result.chartSeries = chartItems;
	result.taskId = source.taskId;

	if (chartItems.length > 0) {
		result.axisLabels = chartItems[0].data.map(({ label }) => label);
	}

	return result;
};

export const transformCustomSummarySource = (
	source: CustomSummaryResponse,
	searchParams: Query,
	intl: IntlShape,
): ChartData => {
	const searchQueryString = isCustomTimeScale(searchParams.timescaleId)
		? `${searchParams.timescaleId}${searchParams.timeBreakdown || ''}`
		: `${searchParams.timescaleId}`;
	const result = formatChartData(source, searchQueryString);

	result.isPercentage = isPercentageChart(source);
	result.yAxisMinMax = getYAxisMinMax(source);
	const formatter = getValueFormatter(source);

	// map source series to chart Item series
	const chartItems = source.series.map((s, seriesIndex) => ({
		type: LINE,
		// @ts-expect-error - TS2339 - Property 'seriesType' does not exist on type 'SeriesEntity'.
		typeKey: s.seriesType.typeKey,
		seriesIndex,
		seriesId: s.seriesId,
		name: s.label,
		summaryValue: formatter(s.seriesSummaryValue),
		itemStyle: {
			color: dynamicColorToToken(s.color),
			borderWidth: 2,
			shadowColor: dynamicColorToToken(s.color),
		},
		showAllSymbol: true,
		emphasis: {
			itemStyle: {
				borderColor: dynamicColorToToken(s.color),
				shadowColor: dynamicColorToToken(s.color),
				borderWidth: 7,
				shadowBlur: 0,
			},
		},
		symbolSize: 9,
		data: s.data.map(({ x, y }, dataIndex) => ({
			id: x,
			seriesId: s.seriesId,
			seriesIndex,
			dataIndex,
			label: getLabel(x, source.timezone, searchQueryString, intl),
			...formatter(y),
			...getMetaData(x, source.timezone, searchQueryString, intl),
		})),
		markLine: getTargetMarkLine(source),
		// @ts-expect-error - TS2339 - Property 'seriesType' does not exist on type 'SeriesEntity'.
		seriesType: s.seriesType,
	}));
	// @ts-expect-error - TS2322 - Type '{ type: string; typeKey: any; seriesIndex: number; seriesId: string; name: string; summaryValue: YAxisData; itemStyle: { color: string; borderWidth: number; shadowColor: string; }; showAllSymbol: boolean; ... 4 more ...; seriesType: any; }[]' is not assignable to type 'Series[]'.
	result.chartSeries = chartItems;

	result.target = source.target;

	if (chartItems.length > 0) {
		result.axisLabels = chartItems[0].data.map(({ label }) => label);
	}

	result.taskId = source.taskId;
	return result;
};
